/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useI18n.ts
 🗓️ Created: 2025-01-05
 🧩 Description:
    Custom hook for internationalization (i18n) functionality
    with language switching and translation management.

 🔧 Notes:
    - Integrates with react-i18next for translation management
    - Supports dynamic language switching across all namespaces
    - Provides translation functions and language utilities
    - Handles language persistence and state management
    - Supports all platform modules and components
    - Implements refresh mechanism for language changes

 🔁 Last Updated: 2025-01-05 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback } from 'react';
import { changeLanguage as changeLang, getCurrentLanguage as getCurrentLang, getAvailableLanguages as getAvailableLangs } from '../i18n';
import { api } from '../lib/api';

export const useI18n = () => {
  const { t, i18n } = useTranslation([
    // Core namespaces
    'common', 'menu', 'auth', 'profile', 'dashboard',
    // Administration modules
    'users', 'profiles', 'roleProfiles', 'logProfiles', 'apiKeys', 'tenants',
    // System settings
    'systemGeneral', 'systemMisc', 'systemVars', 'systemCleanup',
    'branding', 'appearance', 'emailSettings', 'emailTemplates',
    // Telephony servers and domains
    'telephonyServers', 'telephonyDomains', 'certificates',
    // SIP configuration
    'sipProfiles', 'deviceSipProfiles', 'gatewaySipProfiles', 'sipEndpoints',
    // Gateways and routing
    'gateways', 'outboundRoutes', 'inboundRoutes', 'dialRuleRestrictions', 'classOfServices', 'dialProfiles',
    // Time management
    'timeGroups', 'timeConditions',
    // Extensions and features
    'extensions', 'extensionStatus', 'featureCodes', 'featureCategories', 'hotDesking', 'bulkModification',
    // Call center
    'queues', 'queueProfiles', 'agents', 'vipLists', 'queueCallbackReport', 'callbackProfiles', 'callbackRules',
    // Ring groups and call distribution
    'ringGroups', 'pagingGroups', 'pickupGroups', 'parkingLots',
    // IVR and announcements
    'ivrs', 'ivrStats', 'announcements', 'mohSources',
    // Voicemail
    'voicemailSettings', 'voicemailsBroadcastGroups',
    // Conferences
    'conferences', 'conferenceProfiles',
    // Direct routing
    'directDial', 'directRoute', 'dynamicRouting', 'dynamicDestination', 'priorityRouting',
    // PIN and authentication
    'pinLists', 'authenticationCodes', 'authorizationCodes', 'customerCodes',
    // Recording and AI
    'recordingManagement', 'voiceGuides', 'aiProfiles', 'aiProviders', 'aiAgents',
    // CDR and reporting
    'cdrSettings', 'cdrFilters', 'callDetailRecords', 'activeCalls',
    // Logging
    'auditLogs', 'userActivity', 'changeHistory', 'securityLogs', 'loggingSettings', 'logSettings', 'logViewer', 'logs',
    // CRM and caller ID
    'crm', 'callerIdLookupSources', 'cidModifiers',
    // Blacklist and security
    'blacklist', 'weakPasswords', 'accessControl',
    // Speed dials
    'speedDials',
    // Provisioning
    'provisioning', 'provisioningTemplates', 'provisioningSecurity', 'provisioningVendors', 'provisioningDeviceModels', 'provisioningDevices',
    // Backup and restore
    'backupRestore',
    // Network and firewall
    'networkSettings', 'firewallRules', 'firewallServices', 'firewallSettings', 'geoFirewall', 'voipbl',
    // OpenVPN
    'openvpn', 'openvpnClient',
    // ESL
    'eslUsers',
    // Fax
    'faxDevices', 'faxSending', 'faxViewer',
    // ARS
    'ars',
    // Custom applications
    'customApplications', 'customTasks', 'cronProfiles', 'formBuilder',
    // Phonebooks
    'phonebooks',
    // Emergency and special routing
    'emergencyNumbers', 'disaEntries',
    // Languages
    'languages',
    // HTTP and CLI
    'httpServer', 'pbxCli',
    // Call flows
    'callFlows',
    // Dispatch
    'dispatchLocations',
    // SMS
    'sms',
    // Licensing
    'licensing',
    // AI Tool Profiles
    'aiToolProfiles'
  ], {
    useSuspense: false
  } as any); // Specify all namespaces with fallback to common
  const [currentLang, setCurrentLang] = useState(i18n.language);
  const [refreshKey, setRefreshKey] = useState(0);

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      // Language changed
      setCurrentLang(lng);
      setRefreshKey(prev => prev + 1); // Force re-render
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const loadCustomLanguageTranslations = async (locale: string) => {
    try {
      const res = await api.get(`/api/languages/${locale}/translations`);
      if (res.data?.success && res.data.data) {
        const translations = res.data.data;
        Object.entries(translations).forEach(([ns, strings]: [string, any]) => {
          const parsedStrings: Record<string, any> = {};
          Object.entries(strings).forEach(([k, v]: [string, any]) => {
            if (typeof v === 'string' && (v.trim().startsWith('{') || v.trim().startsWith('['))) {
              try {
                parsedStrings[k] = JSON.parse(v);
              } catch {
                parsedStrings[k] = v;
              }
            } else {
              parsedStrings[k] = v;
            }
          });
          i18n.addResourceBundle(locale, ns, parsedStrings, true, true);
        });
      }
    } catch (error) {
      console.error(`Failed to load dynamic translations for locale: ${locale}`, error);
    }
  };

  const changeLanguage = async (locale: string) => {
    try {
      localStorage.setItem('i18nextLng', locale);
      await loadCustomLanguageTranslations(locale);
      await i18n.changeLanguage(locale);
      setCurrentLang(locale);
    } catch (error) {
      console.error('Error changing language:', error);
    }
  };

  // Automatically load custom translations on mount if active language is custom
  useEffect(() => {
    const savedLanguage = localStorage.getItem('i18nextLng') || 'en';
    if (savedLanguage !== 'en' && savedLanguage !== 'es') {
      loadCustomLanguageTranslations(savedLanguage).then(() => {
        i18n.changeLanguage(savedLanguage);
        setCurrentLang(savedLanguage);
      });
    }
  }, []);

  const initializeI18n = async () => {
    try {
      const savedLanguage = localStorage.getItem('i18nextLng') || 'en';
      await changeLanguage(savedLanguage);
    } catch (error) {
      console.error('Failed to initialize i18n:', error);
    }
  };

  const getAvailableLanguages = () => {
    return getAvailableLangs();
  };

  const getCurrentLanguage = () => {
    return currentLang;
  };

  // Helper function to get translation with namespace
  const tWithNS = (namespace: string, key: string): string => {
    return i18n.t(key, { ns: namespace }) as string;
  };

  // Enhanced t function that can handle namespaces
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tEnhanced = useCallback((key: string, optionsOrFallback?: any): string => {
    // Handle fallback string as defaultValue
    const options = typeof optionsOrFallback === 'string'
      ? { defaultValue: optionsOrFallback }
      : (optionsOrFallback || {});

    // If key contains a colon, treat it as namespace:key (e.g., "conferences:fields.extension")
    if (key.includes(':')) {
      const parts = key.split(':');
      if (parts.length >= 2) {
        const namespace = parts[0];
        const keyName = parts.slice(1).join(':');
        return i18n.t(keyName, { ns: namespace, ...options }) as string;
      }
    }
    // If key contains a dot, treat it as namespace.key (legacy format)
    if (key.includes('.')) {
      const parts = key.split('.');
      if (parts.length >= 2) {
        const namespace = parts[0];
        const keyName = parts.slice(1).join('.');
        return i18n.t(keyName, { ns: namespace, ...options }) as string;
      }
    }
    // Otherwise, use the default t function
    return t(key, options) as string;
  }, [t, i18n]);

  return {
    t: tEnhanced,
    tWithNS,
    i18n,
    changeLanguage,
    initializeI18n,
    getAvailableLanguages,
    getCurrentLanguage,
    refreshKey
  };
};
